// DTA Macro Calculator — Phase One
// All calculations run client-side. Nothing is persisted or sent to any backend.

/**
 * Mifflin-St Jeor BMR
 * Men:   BMR = (10 × weight_kg) + (6.25 × height_cm) − (5 × age) + 5
 * Women: BMR = (10 × weight_kg) + (6.25 × height_cm) − (5 × age) − 161
 */
export function calcBMR({ sex, weightKg, heightCm, age }) {
  const base = 10 * weightKg + 6.25 * heightCm - 5 * age;
  return sex === "male" ? base + 5 : base - 161;
}

export const ACTIVITY = {
  sedentary: { label: "Sedentary", mult: 1.2, note: "Little or no structured exercise." },
  light: { label: "Lightly Active", mult: 1.375, note: "Some walking or exercise 1–3 times per week." },
  moderate: { label: "Moderately Active", mult: 1.55, note: "Regular exercise 3–5 times per week." },
  very: { label: "Very Active", mult: 1.725, note: "Hard exercise or physical work most days." },
  extreme: { label: "Extremely Active", mult: 1.9, note: "Very high daily activity or intensive training." },
};

/** TDEE = BMR × activity multiplier */
export function calcTDEE(bmr, activityKey) {
  return bmr * ACTIVITY[activityKey].mult;
}

/**
 * Goal calories.
 * Fat loss: default 500 kcal/day deficit (≈ 0.5 kg/week).
 * Build:    default 300 kcal/day surplus.
 * Maintain: TDEE.
 * weeklyLossKg allows the user to request a specific rate (0.25–1.0 kg/week).
 */
export function calcGoalCalories({ tdee, goal, weeklyLossKg = 0.5 }) {
  if (goal === "lose") {
    // 7700 kcal ≈ 1 kg body fat
    const dailyDeficit = Math.round((weeklyLossKg * 7700) / 7);
    return Math.round(tdee - dailyDeficit);
  }
  if (goal === "build") {
    return Math.round(tdee + 300);
  }
  return Math.round(tdee);
}

/**
 * Macros in grams.
 * Protein varies with goal:
 *   lose:     2.2 g / kg bodyweight
 *   maintain: 1.8 g / kg
 *   build:    2.0 g / kg
 * Fat: 25% of calories (9 kcal/g)
 * Carbs: remainder (4 kcal/g)
 * Floors applied so values never go negative.
 */
export function calcMacros({ weightKg, goalCalories, goal }) {
  const proteinPerKg = goal === "lose" ? 2.2 : goal === "build" ? 2.0 : 1.8;
  const proteinG = Math.round(weightKg * proteinPerKg);
  const fatCalories = Math.round(goalCalories * 0.25);
  const fatG = Math.max(30, Math.round(fatCalories / 9));
  const proteinCalories = proteinG * 4;
  const carbCalories = Math.max(0, goalCalories - proteinCalories - fatG * 9);
  const carbsG = Math.max(0, Math.round(carbCalories / 4));
  return { proteinG, carbsG, fatG };
}

/** Minimum safe calorie floor */
export const SAFETY_FLOOR = { male: 1500, female: 1200 };

/** Unit converters */
export const toKg = {
  kg: (kg) => kg,
  stlb: (st, lb) => st * 6.35029 + lb * 0.453592,
};
export const toCm = {
  cm: (cm) => cm,
  ftin: (ft, inches) => ft * 30.48 + inches * 2.54,
};
